import { useEffect } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import localizationManager from "../pages/localizationManager";

const SUPPORTED_LANGS = ["en", "ja", "zh-TW"];
const LANG_MAP = {
  ja: "ja",
  zh: "zh-TW", // Map 'zh' to 'zh-TW' if browser returns 'zh' or 'zh-CN'
  "zh-TW": "zh-TW",
  "zh-tw": "zh-TW", // Handle lowercase variant
  "zh-CN": "zh-TW", // Fallback for any Chinese to TC as per requirement
};

const LanguageHandler = () => {
  const history = useHistory();
  const location = useLocation();
  const { lang } = useParams(); // Expected to be :lang param from Router

  useEffect(() => {
    const handleLanguageLogic = async () => {
      const savedLang = localStorage.getItem("userLang");
      const currentPath = location.pathname;
      
      // Check if current path has a supported language prefix
      // Routes are defined as /:lang(...) so 'lang' param should catch it.
      // However, if we are on a legacy route (e.g. /products), 'lang' will be undefined or caught by another route.
      
      // Case 0: Redirect legacy /zh/ or /zh-tw/ URLs to /zh-TW/
      if (lang && (lang.toLowerCase() === "zh" || lang.toLowerCase() === "zh-tw")) {
        const currentPath = location.pathname;
        const newPath = currentPath.replace(/^\/(zh|zh-tw)(\/|$)/i, "/zh-TW$2");
        if (newPath !== currentPath) {
          history.replace(newPath + location.search + location.hash);
          await localizationManager.saveSettings("zh-TW");
          return;
        }
      }
      
      // Case 1: URL has language prefix
      if (lang && SUPPORTED_LANGS.includes(lang)) {
        if (localizationManager.currentLang !== lang) {
            await localizationManager.saveSettings(lang);
        }
        return;
      }
      
      // Case 2: Legacy URL (No prefix)
      // Requirement: "Legacy URLs must remain valid... always show English... always canonicalize to /en/..."
      // BUT "Language Resolution Priority": 1. URL Prefix, 2. User Pref, 3. Browser, 4. English.
      // If we are here, URL Prefix is absent.
      
      // First Visit Detection (No saved preference)
      if (!savedLang) {
        const browserLang = navigator.language;
        let targetLang = "en"; // Default fallback

        // Detect
        const shortLang = browserLang.split("-")[0];
        if (LANG_MAP[browserLang]) {
            targetLang = LANG_MAP[browserLang];
        } else if (LANG_MAP[shortLang]) {
            // 'zh' -> 'zh-TW', 'ja' -> 'ja'
            targetLang = LANG_MAP[shortLang];
        } else if (shortLang === 'jp') { // rare but possible
            targetLang = 'ja';
        }
        
        // Redirect logic
        if (targetLang !== "en") {
            // e.g. /product -> /ja/product
            // e.g. / -> /ja/
            const cleanPath = currentPath === "/" ? "" : currentPath;
            history.replace(`/${targetLang}${cleanPath}${location.search}${location.hash}`);
            await localizationManager.saveSettings(targetLang);
            return;
        } else {
            // Browser assumes English
            localStorage.setItem("userLang", "en");
            await localizationManager.saveSettings("en");
            return;
        }
      }

      // If user has saved preference but visits legacy URL
      // Prompt says: "Legacy URLs ... always show English ... should not redirect"
      // So if I have 'ja' pref and visit /product, I see English.
      // But if I visit /, it redirects?
      // "Automatically redirect: / -> /ja/ (if browser is Japanese)" is for FIRST VISIT only.
      
      // For returning users visiting /, we might want to redirect to their pref? 
      // User says "Legacy URLs ... always show English".
      // But / (root) is usually an exception. The user prompts imply / -> /ja/ etc.
      
      // Let's implement strict English for Legacy URLs as requested, EXCEPT for First Visit handled above.
      // So if savedLang exists, we just ensure localizationManager is set to 'en' for consistency on this page.
      if (localizationManager.currentLang !== "en") {
         await localizationManager.saveSettings("en");
      }
    };

    handleLanguageLogic();
  }, [lang, location.pathname, history, location.search, location.hash]);

  return null;
};

export default LanguageHandler;
